/**
 * ChatSessionProvider - app-level persistent chat WS connections.
 *
 * Lifts the WS lifecycle above the router so that navigating between pages
 * does not drop in-flight AI runs. One connection is maintained per module key.
 *
 * Usage:
 *   // In App.tsx - place inside WorkspaceProvider, wrapping LayoutProvider
 *   <ChatSessionProvider>...</ChatSessionProvider>
 *
 *   // In any page component
 *   const chatSession = useModuleChatSession('sales');
 */

import type { NavigationActionPayload } from '@protoss/shared';
import * as React from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  useChatSession,
  type UseChatSessionReturn,
} from '../features/ai-chat/hooks/useChatSession';
import { useChatNavigationRef } from '../features/ai-chat/navigation/ChatNavigationContext';

interface ChatSessionContextValue {
  /**
   * The persistent WORKSPACE-scoped WS session shared by every module - the
   * module key travels per-run (useChatSession.sendRun moduleKey option), not
   * per-connection, so one connection serves sales/product/data alike.
   * Field name kept as `sales` for historical call sites.
   */
  sales: UseChatSessionReturn;
}

// The context object must have a stable identity across Vite HMR re-evaluations.
// If Vite recreates this module, React.createContext() produces a new object.
// The already-mounted ChatSessionProvider supplies the OLD identity while consumers
// read the NEW one - causing the "must be called inside <ChatSessionProvider>" crash.
//
// Solution: store the context on window so it survives module re-evaluation.
// On the first load it is created and cached; on every subsequent HMR update the
// same object is reused, so provider and consumers always share the same identity.
declare global {
  interface Window {
    __chatSessionContext?: React.Context<ChatSessionContextValue | null>;
  }
}

const ChatSessionContext: React.Context<ChatSessionContextValue | null> =
  typeof window !== 'undefined' && window.__chatSessionContext
    ? window.__chatSessionContext
    : (() => {
        const ctx = React.createContext<ChatSessionContextValue | null>(null);
        if (typeof window !== 'undefined') window.__chatSessionContext = ctx;
        return ctx;
      })();

export function ChatSessionProvider({ children }: { children: React.ReactNode }) {
  const { workspace } = useWorkspace();
  // Pass workspaceUuid as the default context so every run in the sales session
  // includes it - allowing the server to resolve workspaceId and route through
  // the orchestrator (with model config) instead of executeRunDirect.
  const salesDefaultContext = React.useMemo(
    () => (workspace?.uuid ? { workspaceUuid: workspace.uuid } : undefined),
    [workspace?.uuid]
  );

  // AI-driven navigation: live agent/auto-mode navigation suggestions route to
  // the global navigation handler (ChatNavigationProvider wraps this provider).
  const chatNavRef = useChatNavigationRef();
  const sessionOptions = React.useMemo(
    () => ({
      onAutoNavigate: (payload: NavigationActionPayload) => chatNavRef?.current?.(payload, 'auto'),
    }),
    [chatNavRef]
  );

  // Each call to useChatSession() establishes one persistent WS connection.
  // These connections survive page navigation because this provider lives above
  // the router in the component tree.
  const sales = useChatSession(salesDefaultContext, sessionOptions);

  const value = React.useMemo<ChatSessionContextValue>(
    () => ({ sales }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `sales` identity changes on each render (it has state) - that is intentional:
    // consumers need to re-render when session state changes (new turns, etc.)
    [sales]
  );

  return <ChatSessionContext.Provider value={value}>{children}</ChatSessionContext.Provider>;
}

/**
 * Return the persistent chat session. The session is workspace-scoped and shared
 * across modules (module key is a per-run option), so every moduleKey returns the
 * same session. Must be called inside <ChatSessionProvider>.
 */
export function useModuleChatSession(_moduleKey: string): UseChatSessionReturn {
  const ctx = React.useContext(ChatSessionContext);
  if (!ctx) {
    throw new Error('useModuleChatSession must be called inside <ChatSessionProvider>');
  }
  return ctx.sales;
}

/**
 * Non-throwing variant for shell-level components that may render outside the
 * provider (e.g. auth pages). Returns null when no provider is mounted.
 */
export function useModuleChatSessionSafe(): UseChatSessionReturn | null {
  const ctx = React.useContext(ChatSessionContext);
  return ctx?.sales ?? null;
}
